import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDemoMode } from './DemoMode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DemoHealthScoreCard({ user, token }) {
  const { language } = useLanguage();
  const { state, openActivateModal } = useDemoMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const t = (hi, en) => (language === 'hi' ? hi : en);

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/demo-mode/health-score`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setData(json);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!state?.is_demo || loading || !data?.is_demo) return null;

  const { score, explored_count, total_modules, modules = [], days_used, days_left, next_milestone } = data;

  const ringColor = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : score >= 25 ? '#f97316' : '#ef4444';
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (score / 100) * circumference;

  return (
    <div className="bg-white border border-orange-200 rounded-2xl p-4 shadow-sm" data-testid="demo-health-score-card">
      <div className="flex items-start gap-4">
        {/* Ring */}
        <div className="shrink-0 relative">
          <svg width="92" height="92" viewBox="0 0 92 92" className="-rotate-90">
            <circle cx="46" cy="46" r={radius} stroke="#f1f5f9" strokeWidth="8" fill="none" />
            <circle
              cx="46" cy="46" r={radius}
              stroke={ringColor} strokeWidth="8" fill="none" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 1s ease-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-xl font-black text-slate-900 leading-none">{score}%</div>
              <div className="text-[9px] text-slate-500 uppercase tracking-wide mt-0.5">{t('Explored', 'Explored')}</div>
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-bold text-slate-900 text-sm sm:text-base">
                🎯 {t('Demo Health Score', 'Demo Health Score')}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {t(`आपने ${explored_count}/${total_modules} modules explore किए`, `${explored_count}/${total_modules} modules explored`)}
                {days_used != null && days_left != null && (
                  <span className="ml-1">· {t(`${days_used} दिन में ${score}%`, `${days_used} days · ${score}%`)}</span>
                )}
              </p>
            </div>
            {days_left != null && days_left <= 3 && (
              <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[10px] font-bold border border-rose-200 whitespace-nowrap">
                ⏰ {days_left}d {t('बचे', 'left')}
              </span>
            )}
          </div>

          {next_milestone && (
            <p className="text-xs text-slate-700 mt-2 bg-orange-50 border border-orange-100 rounded-lg p-2 leading-relaxed">
              💡 {language === 'hi' ? next_milestone.label_hi : next_milestone.label_en}
            </p>
          )}

          {/* Module pills */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {modules.map((m) => (
              <a
                key={m.key}
                href={m.route}
                className={`text-[11px] px-2 py-1 rounded-full border font-medium transition-colors ${
                  m.explored
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
                data-testid={`health-module-${m.key}`}
                title={m.explored ? t('Explored', 'Explored') : t('अभी देखें', 'Check it out')}
              >
                <span className="mr-1" aria-hidden>{m.icon}</span>
                {language === 'hi' ? m.label_hi : m.label_en}
                {m.explored && <span className="ml-1 text-emerald-600">✓</span>}
              </a>
            ))}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => openActivateModal()}
              className="text-xs font-bold px-3 py-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white"
              data-testid="health-activate-btn"
            >
              🚀 {t('अभी Activate करें', 'Activate Now')}
            </button>
            <span className="text-[11px] text-slate-500">
              {t('Software FREE · one-time + maintenance (कुल users slab)', 'Software FREE · one-time + maintenance (total-users slab)')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
